import React from 'react';
import * as Yup from 'yup';
import { FormElement, Label } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import ShareLink from '@dashboard/components/ShareLink';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SettingsDescription from '@dashboard/components/SettingsDescription';
import getSellerDomain from '@dashboard/services/getSellerDomain';
import Divider from '@dashboard/components/Divider';
import { Form } from '@dashboard/components/Formik';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';

const SettingsCustomerPortal = () => {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();

  return (
    <>
      <SettingsContainer>
        <SettingsHeader id="customer-portal">Customer portal</SettingsHeader>

        <SettingsDescription className="mb-4">
          Where your customers can view their purchase history, download files and more.
        </SettingsDescription>

        <FormElement>
          <Label htmlFor="">Customer portal link</Label>
          <div className="flex items-center space-x-4">
            <div className="flex-grow">
              <ShareLink link={`${getSellerDomain(seller)}/portal`} />
            </div>
          </div>
        </FormElement>
      </SettingsContainer>

      <SettingsContainer size="2xl">
        <Divider className="my-8" />
      </SettingsContainer>

      {seller.customerPortal && (
        <>
          <SettingsContainer>
            <SettingsHeader id="cancellations">Subscription cancellations</SettingsHeader>
            <SettingsDescription className="mb-4">
              Allow customers to cancel their subscription
            </SettingsDescription>
            <Form
              initialValues={{
                cancellations: {
                  enabled: seller?.customerPortal?.cancellations?.enabled,
                  cancelSettings: seller?.customerPortal?.cancellations?.cancelSettings,
                },
              }}
              validationSchema={Yup.object().shape({
                cancellations: Yup.object().shape({
                  enabled: Yup.boolean(),
                  cancelSettings: Yup.string().oneOf([
                    'cancel_at_period_end',
                    'cancel_immediately',
                  ]),
                }),
              })}
              onSubmit={async (values, actions) => {
                try {
                  const { data } = await updateSeller({
                    customerPortal: {
                      cancellations: values.cancellations,
                    },
                  });

                  actions.resetForm({
                    values: {
                      cancellations: values.cancellations,
                    },
                  });

                  return {
                    data,
                  };
                } catch (error) {
                  return {
                    error,
                  };
                }
              }}
              fields={[
                {
                  name: 'cancellations.enabled',
                  type: 'checkbox',
                  label: 'Allow customers to cancel their subscription',
                  reduceMargin: true,
                },
                {
                  name: 'cancellations.cancelSettings',
                  required: true,
                  inset: true,
                  type: 'radio',
                  label: 'Cancel settings',
                  options: [
                    {
                      value: 'cancel_at_period_end',
                      label: 'Cancel at the end of the current period',
                    },
                    { value: 'cancel_immediately', label: 'Cancel immediately' },
                  ],
                  visible: values => values.cancellations.enabled,
                },
              ]}
            />
          </SettingsContainer>

          <SettingsContainer size="2xl">
            <Divider className="my-8" />
          </SettingsContainer>

          <SettingsContainer>
            <SettingsHeader id="portal-header-text">Customization</SettingsHeader>
            <Form
              initialValues={{
                portalHeaderText: seller?.customerPortal?.portalHeaderText,
              }}
              validationSchema={Yup.object().shape({
                portalHeaderText: Yup.string(),
              })}
              fields={[
                {
                  name: 'portalHeaderText',
                  type: 'text',
                  label: 'Portal header',
                  tooltip:
                    'Customize the header text on the customer portal. This will override the default text.',
                  placeholder: 'View your payments',
                },
              ]}
              onSubmit={async (values, actions) => {
                try {
                  const { data } = await updateSeller({
                    customerPortal: { portalHeaderText: values.portalHeaderText },
                  });

                  actions.resetForm({
                    values: {
                      portalHeaderText: values.portalHeaderText,
                    },
                  });

                  return {
                    data,
                  };
                } catch (error) {
                  return {
                    error,
                  };
                }
              }}
            />
          </SettingsContainer>
        </>
      )}

      <SettingsContainer size="2xl">
        <Divider className="my-8" />
      </SettingsContainer>

      <SettingsContainer size="xl">
        <SettingsHeader id="how-it-works">How it works</SettingsHeader>
        <div>
          <div>
            <ul className="list-disc list-outside ml-4 my-4 space-y-2">
              <li>
                Make sure your customers have easy access to the above customer portal link (include
                in confirmation email, add to your website, etc.)
              </li>
              <li>
                After your customer enters their email address, they will be sent a magic link to
                their inbox for access
              </li>
              <li>
                Once inside, a customer can view their purchase history, manage subscriptions and
                payment methods, download files and access license keys
              </li>
              <li>
                You can access your customer portal by making a (test) payment with your real email
                address
              </li>
              <li>You can not view your portal as one of your customers</li>
              <li>
                Your customer portal is automatically displayed in your customer's language, based
                on their browser settings.
              </li>
            </ul>

            <Link to="/help/articles/customer-portal" color="default" blank>
              Learn more about the customer portal
            </Link>
          </div>
        </div>
      </SettingsContainer>
    </>
  );
};

export default SettingsCustomerPortal;
